(function deliveryAndShippingCheckoutHotfix() {
  if (window.__mwCheckoutHotfixV4Installed) return;
  window.__mwCheckoutHotfixV4Installed = true;

  const VERSION = '4.3.0-debug';
  const DEBUG = true;
  const observedRoots = new WeakSet();
  const retryDelays = [250, 500, 1000, 2000, 4000];
  let checkoutAttemptStarted = false;
  let checkoutAttemptResetTimer;
  let lastUnlockSignature = '';

  window.__mwCheckoutHotfixDebugLog =
    window.__mwCheckoutHotfixDebugLog || [];

  function debug(event, details = {}) {
    if (!DEBUG) return;
    const entry = {
      time: new Date().toISOString(),
      version: VERSION,
      event,
      ...details,
    };
    window.__mwCheckoutHotfixDebugLog.push(entry);
    if (window.__mwCheckoutHotfixDebugLog.length > 200) {
      window.__mwCheckoutHotfixDebugLog.shift();
    }
    console.info('[MW checkout hotfix]', entry);
  }

  function resetCheckoutAttempt(reason) {
    if (checkoutAttemptResetTimer) {
      window.clearTimeout(checkoutAttemptResetTimer);
      checkoutAttemptResetTimer = undefined;
    }
    const wasStarted = checkoutAttemptStarted;
    checkoutAttemptStarted = false;
    if (wasStarted) debug('checkout-attempt-reset', {reason});
  }

  function startCheckoutAttempt() {
    checkoutAttemptStarted = true;
    if (checkoutAttemptResetTimer) {
      window.clearTimeout(checkoutAttemptResetTimer);
    }
    checkoutAttemptResetTimer = window.setTimeout(
      () => resetCheckoutAttempt('safety-timeout'),
      5000,
    );
  }

  function selectedTabId() {
    for (const widget of document.querySelectorAll('mw-delivery-date')) {
      const selectedTab = widget.shadowRoot?.querySelector(
        '[role="tab"][aria-selected="true"]',
      );
      if (selectedTab?.id) return selectedTab.id;
    }
    return null;
  }

  function selectedMethod() {
    const id = selectedTabId() || '';
      if (id === 'shipping') return 'shipping';
      if (id.startsWith('local-delivery')) return 'delivery';
    return null;
  }

  function checkoutSelectors() {
    return window.mwDeliveryDateSelectors?.checkout_button || [
      '[name="checkout"]',
      '[href="/checkout"]',
      'form[action="/checkout"] input[type="submit"]',
      '.checkout_button',
      '.Button--modal_checkout',
    ];
  }

  function allCheckoutControls() {
    const configured = window.mwDeliveryDateSelectors || {};
    const selectors = checkoutSelectors().concat(configured.buy_it_now || []);
    if (typeof window.mwDeliveryDateFindAllByGroupNameSelectors === 'function') {
      return window
        .mwDeliveryDateFindAllByGroupNameSelectors(selectors)
        .filter((control) => control.getRootNode() === document);
    }
    return Array.from(document.querySelectorAll(selectors.join(',')));
  }

  function controlState() {
    return allCheckoutControls().map((control) => ({
      tag: control.tagName,
      id: control.id || null,
      name: control.getAttribute('name'),
      disabled: 'disabled' in control ? Boolean(control.disabled) : null,
      ariaDisabled: control.getAttribute('aria-disabled'),
      connected: control.isConnected,
      displayed: getComputedStyle(control).display !== 'none',
    }));
  }

  function unlockSelectedCheckout(reason = 'scheduled') {
    const method = selectedMethod();
    const dateDisplayed = deliveryDateIsDisplayed();
    if (method !== 'shipping' && method !== 'delivery') {
      const signature = `skip:${method}:${dateDisplayed}`;
      if (signature !== lastUnlockSignature) {
        lastUnlockSignature = signature;
        debug('unlock-skipped', {reason, method, dateDisplayed});
      }
      return;
    }
    window.mwDeliveryDateCheckoutReady = true;
    allCheckoutControls().forEach((control) => {
      if ('disabled' in control) control.disabled = false;
      control.removeAttribute('disabled');
      control.removeAttribute('aria-disabled');
    });
    const signature = `unlocked:${method}:${dateDisplayed}`;
    if (signature !== lastUnlockSignature) {
      lastUnlockSignature = signature;
      debug('checkout-unlocked', {
        reason,
        method,
        dateDisplayed,
        controls: controlState(),
      });
    }
  }

  function scheduleUnlock(reason = 'scheduled') {
    retryDelays.forEach((delay) =>
      window.setTimeout(() => unlockSelectedCheckout(reason), delay),
    );
  }

  function isRealCheckoutAction(event) {
    const selectors = checkoutSelectors();
    if (event.type === 'submit') {
      const submitter = event.submitter;
      return (
        submitter instanceof HTMLElement &&
        submitter.getRootNode() === document &&
        selectors.some((selector) => submitter.matches(selector))
      );
    }
    return event.composedPath().some(
      (node) =>
        node instanceof HTMLElement &&
        node.getRootNode() === document &&
        selectors.some((selector) => node.matches(selector)),
    );
  }

  function deliveryDateIsDisplayed() {
    return Array.from(document.querySelectorAll('mw-delivery-date')).some(
      (widget) => {
        const root = widget.shadowRoot;
        const selectedTab = root?.querySelector(
          '[role="tab"][aria-selected="true"]',
        );
        if (!selectedTab?.id.startsWith('local-delivery')) return false;
        return Array.from(root.querySelectorAll('button')).some(
          (button) => button.textContent.trim().toLowerCase() === 'change date',
        );
      },
    );
  }

  function openDeliveryDateSelector() {
    for (const widget of document.querySelectorAll('mw-delivery-date')) {
      const root = widget.shadowRoot;
      const selectedTab = root?.querySelector(
        '[role="tab"][aria-selected="true"]',
      );
      if (!selectedTab?.id.startsWith('local-delivery')) continue;

      const dateButton = Array.from(root.querySelectorAll('button')).find(
        (button) => {
          if (button.getAttribute('role') === 'tab') return false;
          const text = button.textContent.trim().toLowerCase();
          return text.includes('date');
        },
      );

      if (dateButton) {
        dateButton.focus();
        dateButton.click();
        debug('delivery-date-selector-opened', {
          buttonText: dateButton.textContent.trim(),
        });
        return true;
      }
    }

    debug('delivery-date-selector-missing');
    return false;
  }

  async function readValidDeliveryCart() {
    const root = window.Shopify?.routes?.root || '/';
    let lastError;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const response = await fetch(root + 'cart.js', {
          credentials: 'same-origin',
          headers: {Accept: 'application/json'},
          cache: 'no-store',
        });
        if (!response.ok) throw new Error('Cart request failed');
        const cart = await response.json();
        const attributes = cart.attributes || {};
        const method = String(attributes['Delivery-Method'] || '');
        const date = String(attributes['Delivery-Date'] || '').trim();
        const locationId = String(attributes['Delivery-Location-Id'] || '').trim();
        const valid = Boolean(
          method.startsWith('localDelivery') && date && locationId,
        );
        debug('delivery-cart-check', {
          attempt: attempt + 1,
          responseStatus: response.status,
          method,
          hasDate: Boolean(date),
          hasLocationId: Boolean(locationId),
          valid,
        });
        if (valid) return cart;
      } catch (error) {
        lastError = error;
        debug('delivery-cart-check-error', {
          attempt: attempt + 1,
          message: error instanceof Error ? error.message : String(error),
        });
      }
      await new Promise((resolve) => window.setTimeout(resolve, 300));
    }
    if (lastError) console.warn('[MW checkout hotfix]', lastError);
    return null;
  }

  function redirectToCheckout() {
    window.mwDeliveryDateCheckoutReady = true;
    const root = window.Shopify?.routes?.root || '/';
    debug('checkout-redirect', {method: selectedMethod(), target: root + 'checkout'});
    window.location.assign(root + 'checkout');
  }

  async function proceedToCheckout(event) {
    const realCheckoutAction = isRealCheckoutAction(event);
    if (!realCheckoutAction) return;
    const method = selectedMethod();
    debug('checkout-action', {
      eventType: event.type,
      method,
      selectedTabId: selectedTabId(),
      dateDisplayed: deliveryDateIsDisplayed(),
      checkoutAttemptStarted,
      ready: window.mwDeliveryDateCheckoutReady,
      controls: controlState(),
    });
    if (checkoutAttemptStarted) return;
    if (method === 'shipping') {
      startCheckoutAttempt();
      event.preventDefault();
      event.stopImmediatePropagation();
      redirectToCheckout();
      return;
    }
    if (method !== 'delivery') {
      debug('checkout-left-to-app-validation', {method});
      return;
    }

    startCheckoutAttempt();
    event.preventDefault();
    event.stopImmediatePropagation();
    const validDeliveryCart = await readValidDeliveryCart();
    if (validDeliveryCart) {
      redirectToCheckout();
      return;
    }

    resetCheckoutAttempt('delivery-cart-invalid');
    debug('delivery-checkout-blocked', {
      reason: 'required cart attributes were not saved',
    });
    if (!openDeliveryDateSelector()) {
      window.alert(
        'Please select a delivery date before continuing to checkout.',
      );
    }
  }

  function observeWidgets() {
    document.querySelectorAll('mw-delivery-date').forEach((widget) => {
      const root = widget.shadowRoot;
      if (!root || observedRoots.has(root)) return;
      observedRoots.add(root);
      debug('widget-observed', {widgetCount: document.querySelectorAll('mw-delivery-date').length});
      root.addEventListener('click', (event) => {
        const tab = event.composedPath().find(
          (node) =>
            node instanceof HTMLElement &&
            node.getAttribute('role') === 'tab',
        );
        if (tab?.id) {
          resetCheckoutAttempt(`tab-change:${tab.id}`);
          debug('tab-click', {tabId: tab.id});
          scheduleUnlock(`tab-click:${tab.id}`);
        }
      });
      new MutationObserver(() => scheduleUnlock('widget-mutation')).observe(root, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['aria-selected'],
      });
    });
  }

  window.addEventListener('click', proceedToCheckout, true);
  window.addEventListener('submit', proceedToCheckout, true);
  new MutationObserver(() => {
    observeWidgets();
    scheduleUnlock('document-mutation');
  }).observe(document.documentElement, {childList: true, subtree: true});

  let attempts = 0;
  const startup = window.setInterval(() => {
    observeWidgets();
    scheduleUnlock('startup');
    attempts += 1;
    if (attempts >= 60) window.clearInterval(startup);
  }, 250);

  window.addEventListener('pagehide', (event) => {
    resetCheckoutAttempt(
      event.persisted ? 'pagehide-bfcache' : 'pagehide-navigation',
    );
    debug('pagehide', {persisted: event.persisted});
  });

  window.addEventListener('pageshow', (event) => {
    resetCheckoutAttempt(
      event.persisted ? 'pageshow-bfcache' : 'pageshow-navigation',
    );
    observeWidgets();
    scheduleUnlock(
      event.persisted ? 'pageshow-bfcache' : 'pageshow-navigation',
    );
    debug('pageshow', {
      persisted: event.persisted,
      selectedTabId: selectedTabId(),
      method: selectedMethod(),
      dateDisplayed: deliveryDateIsDisplayed(),
      controls: controlState(),
    });
  });

  window.mwCheckoutHotfixDebug = async function mwCheckoutHotfixDebug() {
    const root = window.Shopify?.routes?.root || '/';
    let cartSummary = {error: 'Cart not read'};
    try {
      const response = await fetch(root + 'cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      const cart = await response.json();
      const attributes = cart.attributes || {};
      cartSummary = {
        itemCount: cart.item_count,
        method: attributes['Delivery-Method'] || null,
        hasDate: Boolean(attributes['Delivery-Date']),
        hasTime: Boolean(attributes['Delivery-Time']),
        hasLocation: Boolean(attributes['Delivery-Location']),
        hasLocationId: Boolean(attributes['Delivery-Location-Id']),
        profileId: attributes['Profile-Id'] || null,
      };
    } catch (error) {
      cartSummary = {
        error: error instanceof Error ? error.message : String(error),
      };
    }
    const result = {
      version: VERSION,
      selectedTabId: selectedTabId(),
      method: selectedMethod(),
      dateDisplayed: deliveryDateIsDisplayed(),
      ready: window.mwDeliveryDateCheckoutReady,
      checkoutAttemptStarted,
      controls: controlState(),
      cart: cartSummary,
      recentEvents: window.__mwCheckoutHotfixDebugLog.slice(-30),
    };
    console.info('[MW checkout hotfix snapshot]', result);
    return result;
  };

  debug('installed', {
    script: document.currentScript?.src || null,
    page: window.location.pathname,
  });
})();
